/* Hallmark · macrostructure: Long Document · tone: calm-operational · anchor hue: heritage green
 * pre-emit critique: P5 H5 E5 S5 R5 V4 · contrast: pass · nav: none · footer: none
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { Program, ProgramRegistration } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { CityAutocomplete } from '../components/CityAutocomplete';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useRegistrationProgram } from '../hooks/usePrograms';
import { apiBaseUrl, apiClient } from '../lib/api-client';
import { getNeonAuthClient } from '../lib/neon-auth';
import { useLanguage } from '../lib/LanguageContext';

const publicRegistrationSchema = z
  .object({
    name: z.string().trim().min(2, 'fieldInvalid'),
    email: z.string().trim().email('fieldInvalid'),
    phone: z.string().trim().min(10, 'fieldInvalid'),
    gender: z.enum(['MALE', 'FEMALE'], { message: 'fieldInvalid' }),
    password: z.string().min(8, 'fieldInvalid'),
    confirmPassword: z.string().min(8, 'fieldInvalid'),
    domisili: z.string().trim().min(2, 'fieldInvalid'),
    reason: z.string().trim().min(10, 'fieldInvalid'),
    consent: z.boolean().refine(value => value, { message: 'consentRequired' }),
  })
  .refine(values => values.password === values.confirmPassword, {
    message: 'passwordMismatch',
    path: ['confirmPassword'],
  });

type PublicRegistrationInput = z.infer<typeof publicRegistrationSchema>;
type SubmissionState = Pick<ProgramRegistration, 'id' | 'status'>;

function isRegistrationAvailable(program: Pick<Program, 'isRegistrationOpen'>) {
  return program.isRegistrationOpen;
}

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message.trim()) return error.message;
  return null;
}

export function PublicProgramRegistration() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { data: program, isLoading, isError } = useRegistrationProgram(code || '');
  const [submission, setSubmission] = useState<SubmissionState | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isExistingAccount, setIsExistingAccount] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PublicRegistrationInput>({
    resolver: zodResolver(publicRegistrationSchema),
    defaultValues: { consent: false, gender: undefined },
  });

  const domisili = watch('domisili') || '';
  const selectedGender = watch('gender');
  const isAvailable = program ? isRegistrationAvailable(program) : false;
  const locale = language === 'ar' ? 'ar-SA' : 'id-ID';

  const registrationWindow = useMemo(() => {
    if (!program) return t(copy.common.tbd);
    const formatDate = (date: Date | string | null | undefined) =>
      date
        ? new Date(date).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : t(copy.common.tbd);
    return `${formatDate(program.registrationOpensAt)} — ${formatDate(program.registrationClosesAt)}`;
  }, [locale, program, t]);

  const fieldError = (key: keyof typeof errors) => {
    const message = errors[key]?.message;
    if (message === 'passwordMismatch') return t(copy.publicRegistration.passwordMismatch);
    if (message === 'consentRequired') return t(copy.publicRegistration.consentRequired);
    return message ? t(copy.publicRegistration.fieldInvalid) : undefined;
  };

  const continueWithExistingAccount = () => {
    if (!program) return;
    void navigate('/login', {
      state: { from: { pathname: `/programs/${encodeURIComponent(program.code)}/register` } },
    });
  };

  const submit = async (values: PublicRegistrationInput) => {
    if (!program || !isAvailable) return;

    setSubmitError(null);
    setIsExistingAccount(false);

    try {
      const signUp = await getNeonAuthClient().signUp.email({
        email: values.email.trim().toLowerCase(),
        password: values.password,
        name: values.name.trim(),
        callbackURL: `${window.location.origin}/verify-email`,
      });

      if (signUp.error) {
        const errorMessage = signUp.error.message || '';
        if (/already|exists|registered|duplicate/i.test(errorMessage)) {
          setIsExistingAccount(true);
          return;
        }
        throw new Error(errorMessage || t(copy.publicRegistration.unexpectedError));
      }

      const registration = await apiClient.registrations.create({
        programId: program.id,
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        gender: values.gender,
        domisili: values.domisili.trim(),
        reason: values.reason.trim(),
        consentGiven: values.consent,
      });

      setSubmission({ id: registration.id, status: registration.status });
    } catch (error) {
      setSubmitError(getErrorMessage(error) || t(copy.publicRegistration.unexpectedError));
    }
  };

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6">
        <div
          className="flex flex-col items-center gap-3 text-on-surface-variant"
          aria-live="polite"
        >
          <Loader2 className="size-8 animate-spin text-primary" aria-hidden="true" />
          <Bilingual {...copy.registration.preparing} />
        </div>
      </main>
    );
  }

  if (isError || !program) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <CircleAlert className="mx-auto size-10 text-error" aria-hidden="true" />
          <h1 className="mt-5 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface">
            <Bilingual {...copy.publicRegistration.invalidLinkTitle} />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual {...copy.publicRegistration.invalidLinkDesc} />
          </p>
          <Link
            to="/login"
            className="mt-7 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          >
            <Bilingual {...copy.publicRegistration.loginAfterVerification} />
          </Link>
        </section>
      </main>
    );
  }

  if (!isAvailable) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <Clock3 className="mx-auto size-10 text-warning" aria-hidden="true" />
          <p className="mt-5 font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
            {program.code}
          </p>
          <h1 className="mt-3 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface">
            <Bilingual {...copy.publicRegistration.unavailableTitle} />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual {...copy.publicRegistration.unavailableDesc} />
          </p>
        </section>
      </main>
    );
  }

  if (submission) {
    const isAutoApproved = submission.status === 'APPROVED';
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-success-container text-success">
            <CheckCircle2 className="size-8" aria-hidden="true" />
          </div>
          <p className="mt-5 font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
            {program.name}
          </p>
          <h1 className="mt-3 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface">
            <Bilingual
              {...(isAutoApproved
                ? copy.publicRegistration.successAutoTitle
                : copy.publicRegistration.successManualTitle)}
            />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual
              {...(isAutoApproved
                ? copy.publicRegistration.successAutoDesc
                : copy.publicRegistration.successManualDesc)}
            />
          </p>
          <Link
            to="/login"
            className="mt-7 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          >
            <Bilingual {...copy.publicRegistration.loginAfterVerification} />
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-surface px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-6xl">
        <header className="rounded-[2rem] bg-surface-container-low p-6 shadow-level1 sm:p-9">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end">
            <div className="min-w-0">
              <p className="font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
                <Bilingual {...copy.publicRegistration.eyebrow} />
              </p>
              <h1 className="mt-3 overflow-wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
                <Bilingual {...copy.publicRegistration.titlePrefix} />
                <span className="block text-primary">{program.name}</span>
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-on-surface-variant sm:text-base">
                {program.description || <Bilingual {...copy.publicRegistration.subtitle} />}
              </p>
            </div>
            {program.coverImage ? (
              <img
                src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                alt={program.name}
                width={576}
                height={324}
                className="aspect-[16/9] w-full rounded-2xl object-cover shadow-level2"
              />
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <UsersRound className="size-10" aria-hidden="true" />
              </div>
            )}
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <form onSubmit={handleSubmit(submit)} className="space-y-6">
            <section className="rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                  <UserRound size={20} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.accountSection} />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual {...copy.publicRegistration.subtitle} />
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Input
                  label={t(copy.publicRegistration.nameLabel)}
                  error={fieldError('name')}
                  leftIcon={<UserRound size={17} aria-hidden="true" />}
                  {...register('name')}
                />
                <Input
                  label={t(copy.publicRegistration.emailLabel)}
                  type="email"
                  error={fieldError('email')}
                  leftIcon={<Mail size={17} aria-hidden="true" />}
                  {...register('email')}
                />
                <Input
                  label={t(copy.publicRegistration.phoneLabel)}
                  type="tel"
                  error={fieldError('phone')}
                  leftIcon={<Phone size={17} aria-hidden="true" />}
                  {...register('phone')}
                />
                <fieldset className="min-w-0">
                  <legend className="mb-2 block text-sm font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.genderLabel} />
                  </legend>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'MALE' as const, label: copy.publicRegistration.male },
                      { value: 'FEMALE' as const, label: copy.publicRegistration.female },
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex min-h-11 cursor-pointer items-center justify-center rounded-xl px-3 text-sm font-semibold transition-colors duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-primary/20 ${
                          selectedGender === option.value
                            ? 'bg-primary text-on-primary'
                            : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                        }`}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          className="sr-only"
                          {...register('gender')}
                        />
                        <Bilingual {...option.label} />
                      </label>
                    ))}
                  </div>
                  {fieldError('gender') && (
                    <p className="mt-2 text-xs text-error">{fieldError('gender')}</p>
                  )}
                </fieldset>
                <Input
                  label={t(copy.publicRegistration.passwordLabel)}
                  type="password"
                  error={fieldError('password')}
                  leftIcon={<LockKeyhole size={17} aria-hidden="true" />}
                  {...register('password')}
                />
                <Input
                  label={t(copy.publicRegistration.confirmPasswordLabel)}
                  type="password"
                  error={fieldError('confirmPassword')}
                  leftIcon={<LockKeyhole size={17} aria-hidden="true" />}
                  {...register('confirmPassword')}
                />
              </div>
            </section>

            <section className="rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-secondary-container p-2.5 text-on-secondary-container">
                  <MapPin size={20} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.profileSection} />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual {...copy.registration.verifyIdentityDesc} />
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-5">
                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-on-surface"
                    htmlFor="public-registration-city"
                  >
                    <Bilingual {...copy.publicRegistration.cityLabel} />
                  </label>
                  <CityAutocomplete
                    id="public-registration-city"
                    value={domisili}
                    onChange={value => setValue('domisili', value, { shouldValidate: true })}
                    placeholder={t(copy.registration.cityPlaceholder)}
                    dropdownLabel={t(copy.registration.cityDropdownLabel)}
                    emptyMessage={query =>
                      t(copy.registration.cityNoResults).replace('{query}', query)
                    }
                    error={fieldError('domisili')}
                    errorId="public-registration-city-error"
                  />
                  {fieldError('domisili') && (
                    <p
                      id="public-registration-city-error"
                      role="alert"
                      className="mt-2 text-xs text-error"
                    >
                      {fieldError('domisili')}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-on-surface"
                    htmlFor="public-registration-reason"
                  >
                    <Bilingual {...copy.publicRegistration.reasonLabel} />
                  </label>
                  <textarea
                    id="public-registration-reason"
                    rows={5}
                    className="min-h-28 w-full rounded-2xl bg-surface-container-low px-4 py-3 text-sm leading-6 text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                    placeholder={t(copy.publicRegistration.reasonPlaceholder)}
                    aria-invalid={Boolean(fieldError('reason'))}
                    aria-describedby={
                      fieldError('reason') ? 'public-registration-reason-error' : undefined
                    }
                    {...register('reason')}
                  />
                  {fieldError('reason') && (
                    <p
                      id="public-registration-reason-error"
                      role="alert"
                      className="mt-2 text-xs text-error"
                    >
                      {fieldError('reason')}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-3xl bg-surface-container-low p-5 shadow-level1 sm:p-7">
              {submitError && (
                <div
                  role="alert"
                  className="mb-5 rounded-2xl bg-error-container p-4 text-sm text-on-error-container"
                >
                  {submitError}
                </div>
              )}
              {isExistingAccount && (
                <div
                  role="status"
                  className="mb-5 rounded-2xl bg-warning-container p-4 text-sm text-on-warning-container"
                >
                  <p>{t(copy.publicRegistration.accountExists)}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={continueWithExistingAccount}
                    className="mt-3 min-h-10"
                  >
                    <Bilingual {...copy.publicRegistration.loginToContinue} />
                  </Button>
                </div>
              )}
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-surface-container-lowest p-4 focus-within:outline focus-within:outline-2 focus-within:outline-primary/20">
                <input
                  type="checkbox"
                  className="mt-0.5 size-5 shrink-0 accent-primary"
                  aria-invalid={Boolean(fieldError('consent'))}
                  {...register('consent')}
                />
                <span className="text-sm leading-6 text-on-surface">
                  <Bilingual {...copy.publicRegistration.consent} />
                </span>
              </label>
              {fieldError('consent') && (
                <p className="mt-2 text-xs text-error">{fieldError('consent')}</p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isSubmitting}
                className="mt-5 min-h-12 w-full justify-center gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin" size={18} aria-hidden="true" />
                ) : (
                  <ArrowRight size={18} aria-hidden="true" />
                )}
                <Bilingual
                  {...(isSubmitting
                    ? copy.publicRegistration.submitting
                    : copy.publicRegistration.submit)}
                />
              </Button>
            </section>
          </form>

          <aside className="rounded-3xl bg-surface-container-low p-5 shadow-level1 lg:sticky lg:top-6">
            <div className="flex items-center gap-2 text-primary">
              <ShieldCheck size={18} aria-hidden="true" />
              <h2 className="font-headline text-base font-bold text-on-surface">
                <Bilingual {...copy.publicRegistration.programInfo} />
              </h2>
            </div>
            <dl className="mt-5 space-y-3">
              <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {program.code}
                </dt>
                <dd className="mt-1 overflow-wrap-anywhere text-sm font-semibold text-on-surface">
                  {program.name}
                </dd>
              </div>
              <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                <dt className="flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  <CalendarDays size={13} aria-hidden="true" />
                  <Bilingual {...copy.publicRegistration.scheduleLabel} />
                </dt>
                <dd className="mt-1 text-sm font-semibold text-on-surface">{registrationWindow}</dd>
              </div>
              {program.startDate && (
                <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                  <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.publicRegistration.startDateLabel} />
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-on-surface">
                    {new Date(program.startDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </dd>
                </div>
              )}
            </dl>
            <div
              className={`mt-5 rounded-2xl p-4 text-sm leading-6 ${program.approvalMode === 'AUTO_APPROVE' ? 'bg-success-container text-on-success-container' : 'bg-warning-container text-on-warning-container'}`}
            >
              <p className="font-bold">
                <Bilingual
                  {...(program.approvalMode === 'AUTO_APPROVE'
                    ? copy.publicRegistration.directEnrollment
                    : copy.publicRegistration.manualReview)}
                />
              </p>
              <p className="mt-1 opacity-80">
                <Bilingual
                  {...(program.approvalMode === 'AUTO_APPROVE'
                    ? copy.registration.successApprovedDesc
                    : copy.registration.successPendingDesc)}
                />
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
